import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from './authFetchHeaders';

/**
 * The sign-in disclaimer is accepted once per account, for good.
 *
 * The server keeps the acceptance (/api/auth/disclaimer); this browser keeps a
 * copy so the check is instant. It used to live ONLY in this browser, keyed by
 * the email exactly as the login returned it, so it came back on a new device
 * or browser, in a private window, after site data was cleared (Safari does
 * that after 7 days away), on another brand's domain, or when the email came
 * back with different capitals.
 */

const keyFor = (email) => `disclaimer_accepted_${String(email || '').trim().toLowerCase()}`;
const dateKeyFor = (email) => `disclaimer_accepted_date_${String(email || '').trim().toLowerCase()}`;

/** The value of a key saved under any capitalization of this email (older keys kept the login's casing). */
function readAnyCase(prefix, email) {
  const want = `${prefix}${String(email || '').trim().toLowerCase()}`;
  try {
    const exact = localStorage.getItem(want);
    if (exact != null) return exact;
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.toLowerCase() === want) return localStorage.getItem(k);
    }
  } catch (_e) { /* storage unavailable */ }
  return null;
}

/** Accepted in this browser, whatever the email's capitals were when it was saved. */
export function hasLocalAcceptance(email) {
  if (!String(email || '').trim()) return false;
  return readAnyCase('disclaimer_accepted_', email) === 'true';
}

function localAcceptedAt(email) {
  return readAnyCase('disclaimer_accepted_date_', email);
}

export function rememberLocally(email, acceptedAt = new Date().toISOString()) {
  try {
    localStorage.setItem(keyFor(email), 'true');
    if (!localStorage.getItem(dateKeyFor(email))) localStorage.setItem(dateKeyFor(email), String(acceptedAt || new Date().toISOString()));
  } catch (_e) { /* private mode: the server still has it */ }
}

/** true / false from the server; null when it couldn't be asked. */
export async function fetchServerAcceptance() {
  try {
    const res = await fetch(API_ENDPOINTS.authDisclaimer, { headers: getBearerAuthHeaders() });
    if (!res.ok) return null;
    const body = await res.json().catch(() => ({}));
    if (!body.success) return null;
    return { accepted: Boolean(body.accepted), acceptedAt: body.acceptedAt || null };
  } catch (_e) {
    return null;
  }
}

/** Save the acceptance on the server (the first date is kept). Resolves true when saved. */
export async function saveServerAcceptance(acceptedAt = null) {
  try {
    const res = await fetch(API_ENDPOINTS.authDisclaimerAccept, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
      body: JSON.stringify({ acceptedAt }),
    });
    return res.ok;
  } catch (_e) {
    return false;
  }
}

const synced = new Set();
/**
 * This browser accepted (maybe before the server kept acceptances): make sure
 * the server knows, once per page load, so other devices never ask again.
 */
export function syncLocalAcceptance(email) {
  const key = String(email || '').trim().toLowerCase();
  if (!key || synced.has(key)) return;
  synced.add(key);
  saveServerAcceptance(localAcceptedAt(email)).then((ok) => { if (!ok) synced.delete(key); });
}

/**
 * Should the disclaimer show for this signed-in email? Accepted here or on the
 * server: never. Only a clear "not accepted" from the server shows it. When the
 * server can't be asked, it waits for the next load rather than risk asking
 * someone who already accepted (a new account is asked as soon as it answers).
 */
export async function shouldShowDisclaimer(email) {
  if (!String(email || '').trim()) return false;
  if (hasLocalAcceptance(email)) {
    syncLocalAcceptance(email);
    return false;
  }
  const server = await fetchServerAcceptance();
  if (!server) return false;
  if (server.accepted) {
    rememberLocally(email, server.acceptedAt || undefined);
    return false;
  }
  return true;
}

/** The user accepted: remember it here and on the server. */
export function acceptDisclaimer(email) {
  const now = new Date().toISOString();
  rememberLocally(email, now);
  saveServerAcceptance(now).then((ok) => {
    if (ok) synced.add(String(email || '').trim().toLowerCase());
  });
}
